import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import useSWR from 'swr';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import CustomInput from 'components/CustomInput/CustomInput';
import api, { eventApi, fetcher } from 'lib/api';
import styles from './EventResponses.module.scss';

const EventResponses = ({ t }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data, mutate, isLoading } = useSWR(eventApi.responses(id), fetcher);
    const payload = data?.results || {};
    const event = payload.event || {};
    const stats = payload.stats || {};
    const rows = payload.responses || [];
    const [query, setQuery] = useState('');
    const [manualUserId, setManualUserId] = useState('');
    const [notifyOpen, setNotifyOpen] = useState(false);
    const [notifyCount, setNotifyCount] = useState(null);
    const [notifyBillable, setNotifyBillable] = useState(false);
    const [notifyTitle, setNotifyTitle] = useState('');
    const [notifyBody, setNotifyBody] = useState('');
    const [notifyAudience, setNotifyAudience] = useState('registered');
    const [error, setError] = useState('');

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) =>
            [row.name, row.email, row.kennitala, row.phone, row.status]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(q)
        );
    }, [rows, query]);

    const handleCsv = async () => {
        const res = await api.get(eventApi.responsesCsv(id), { responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vidburdur-${event.number || id}-skraningar.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const checkIn = async (userId) => {
        setError('');
        try {
            await fetcher(eventApi.attendance(id), 'POST', { torgid_user_id: userId });
            mutate();
        } catch (err) {
            setError(err?.response?.data?.message || t('events.attendance_failed'));
        }
    };

    const undo = async (userId) => {
        setError('');
        try {
            await fetcher(eventApi.undoAttendance(id, userId), 'DELETE');
            mutate();
        } catch (err) {
            setError(err?.response?.data?.message || t('events.attendance_failed'));
        }
    };

    const loadNotifyCount = async (audience) => {
        const res = await fetcher(eventApi.notificationCount(id, { audience }));
        setNotifyCount(res?.results?.count ?? 0);
        setNotifyBillable(!!res?.results?.is_billable);
    };

    const openNotify = async () => {
        setError('');
        try {
            await loadNotifyCount(notifyAudience);
            setNotifyTitle(t('events.notify_default_title', { title: event.title }));
            setNotifyBody(event.title || '');
            setNotifyOpen(true);
        } catch (err) {
            setError(err?.response?.data?.message || t('events.notify_failed'));
        }
    };

    const sendNotify = async () => {
        setError('');
        try {
            await fetcher(eventApi.notificationSend(id), 'POST', {
                title: notifyTitle,
                body: notifyBody,
                audience: notifyAudience,
            });
            setNotifyOpen(false);
        } catch (err) {
            setError(err?.response?.data?.message || t('events.notify_failed'));
        }
    };

    return (
        <div className={styles.page}>
            <SparaTitle
                title={t('events.list_title', { number: event.number || '', title: event.title || '' })}
                rightComponent={
                    <div className={styles.headerActions}>
                        <SparaButton variant="outlined" onClick={() => navigate(`/events/${id}`)}>
                            {t('edit')}
                        </SparaButton>
                        <SparaButton variant="outlined" onClick={handleCsv}>
                            {t('events.csv')}
                        </SparaButton>
                        <SparaButton variant="outlined" onClick={openNotify}>
                            {t('events.notify')}
                        </SparaButton>
                    </div>
                }
            />
            <p className={styles.stats}>
                {t('events.stats_line', {
                    registered: stats.registered_count ?? 0,
                    waitlisted: stats.waitlisted_count ?? 0,
                    attended: stats.attended_count ?? 0,
                    capacity: stats.capacity ?? t('events.unlimited'),
                })}
            </p>
            {error ? <p className={styles.error}>{error}</p> : null}
            <div className={styles.toolbar}>
                <CustomInput
                    labelText={t('events.search')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: query,
                        onChange: (e) => setQuery(e.target.value),
                    }}
                />
                <div className={styles.manual}>
                    <CustomInput
                        labelText={t('events.manual_user_id')}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                            value: manualUserId,
                            onChange: (e) => setManualUserId(e.target.value),
                        }}
                    />
                    <SparaButton
                        size="small"
                        onClick={() => {
                            if (manualUserId) checkIn(Number(manualUserId));
                        }}
                    >
                        {t('events.manual_checkin')}
                    </SparaButton>
                </div>
            </div>
            {isLoading ? <p>{t('events.loading')}</p> : null}
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{t('events.col_name')}</th>
                            <th>{t('events.col_kennitala')}</th>
                            <th>{t('events.col_status')}</th>
                            <th>{t('events.col_attended')}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((row) => (
                            <tr key={`${row.torgid_user_id}-${row.status}-${row.attended_at || ''}`}>
                                <td>
                                    {row.name}
                                    <div className={styles.sub}>{row.email}</div>
                                </td>
                                <td>{row.kennitala}</td>
                                <td>{t(`events.status_${row.status}`, { defaultValue: row.status })}</td>
                                <td>
                                    {row.attended
                                        ? t('events.attended_yes', { time: row.attended_at || '' })
                                        : t('events.attended_no')}
                                </td>
                                <td>
                                    {row.torgid_user_id && row.attended ? (
                                        <SparaButton
                                            variant="outlined"
                                            size="small"
                                            onClick={() => undo(row.torgid_user_id)}
                                        >
                                            {t('events.undo_checkin')}
                                        </SparaButton>
                                    ) : null}
                                    {row.torgid_user_id && !row.attended && row.status === 'registered' ? (
                                        <SparaButton
                                            size="small"
                                            onClick={() => checkIn(row.torgid_user_id)}
                                        >
                                            {t('events.mark_attended')}
                                        </SparaButton>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmDialog
                open={notifyOpen}
                title={t('events.notify_title')}
                message={
                    notifyBillable
                        ? t('events.notify_billable', { count: notifyCount ?? 0 })
                        : t('events.notify_count', { count: notifyCount ?? 0 })
                }
                onConfirm={sendNotify}
                onCancel={() => setNotifyOpen(false)}
                confirmText={t('events.notify_send')}
            >
                <div className={styles.notifyFields}>
                    <label className={styles.audienceLabel}>
                        {t('events.notify_audience')}
                        <select
                            className={styles.audienceSelect}
                            value={notifyAudience}
                            onChange={async (e) => {
                                const next = e.target.value;
                                setNotifyAudience(next);
                                try {
                                    await loadNotifyCount(next);
                                } catch (err) {
                                    setError(err?.response?.data?.message || t('events.notify_failed'));
                                }
                            }}
                        >
                            <option value="registered">{t('events.notify_audience_registered')}</option>
                            <option value="waitlisted">{t('events.notify_audience_waitlisted')}</option>
                        </select>
                    </label>
                    <CustomInput
                        labelText={t('events.notify_message_title')}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                            value: notifyTitle,
                            onChange: (e) => setNotifyTitle(e.target.value),
                        }}
                    />
                    <CustomInput
                        labelText={t('events.notify_message_body')}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                            multiline: true,
                            rows: 3,
                            value: notifyBody,
                            onChange: (e) => setNotifyBody(e.target.value),
                        }}
                    />
                </div>
            </ConfirmDialog>
        </div>
    );
};

export default withTranslation()(EventResponses);
